import { Component, Input, OnInit, ViewEncapsulation, ViewChild, Injector, Inject, Injectable, inject, signal, input, effect, ElementRef } from '@angular/core';
import { trigger, state, style, animate, transition, query } from '@angular/animations';

// import { UntypedFormControl } from '@angular/forms';
import { Observable, of, Subscription, Subject, firstValueFrom, lastValueFrom } from 'rxjs';
import { catchError, startWith, switchMap, take, tap } from 'rxjs/operators';

import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { transform } from 'ol/proj';

import { MapService } from '../../services/map.service';
import { ViewService } from '../../services/view.service';
import { MarkerService } from '../../services/marker.service';
import { PopupService } from '../../services/popup.service';
import { CoordinatesService } from '../../services/coordinates.service';
import { PopupData } from '../../interfaces/popup-data';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { MatInput, MatInputModule } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MapsheetsService } from '../../services/mapsheets.service';
import { TTCoordinate } from '../../models/tt-coordinate';
import { TT_LIB_CONFIG } from '../../tt-lib.config.token';
import { AppConfig } from '../../interfaces/app-config';
import { OlTooltip } from '../../models/ol-tooltip';
import { MapindexService } from '../../services/mapindex.service';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from '@tt-lib/material.module';
import { ToggleEventNameType, ToggleEventService, ToggleEventType } from '@tt-lib/services/toggle-event.service';
import { BackEventService } from '@tt-lib/services/back-event.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { ControlService } from '@tt-lib/services/control.service';
import * as olCoordinate from 'ol/coordinate';
import { AlertService } from '@tt-lib/services/alert.service';
import { BrowserSnifferService } from '@tt-lib/services/browser-sniffer.service';
import { MapBrowserEvent } from 'ol';

@Component({
  selector: 'tt-geocoding',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatAutocompleteModule,
    MatInputModule,
    MaterialModule
  ],
  templateUrl: './geocoding.component.html',
  styleUrls: ['./geocoding.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('preventInitialChildAnimations', [
      transition(':enter', [
        query(':enter', [], { optional: true })
      ])
    ]),
    trigger('openClose', [
      state('open', style({
        opacity: 1.0,
        transform: 'scale(1.0)',
      })),
      //enter dom
      transition(':enter', [
        style({ opacity: 1.0, transform: 'scale(0.8)' }),
        animate('0.2s cubic-bezier(0.0, 0.0, 0.2, 1)', style({ opacity: 1, transform: 'scale(1.0)' })),
      ]),
      //leave dom
      transition(':leave', [
        animate('0.1s cubic-bezier(0.0, 0.0, 0.2, 1)', style({ opacity: 0, transform: 'scale(0.8)' })),
      ]),
    ]),
    trigger('showHideTip', [
      transition(':enter', [
        style({
          opacity: 0.0,
          // transform: 'scale(0.3)',
          transform: 'translateY( -30px )'
        }),
        animate('0.2s cubic-bezier(0.0, 0.0, 0.2, 1)', style({
          opacity: 1,
          // transform: 'scale(1.0)',
          transform: 'translateY( 0px )'
        })),
      ])
    ])
  ],
  standalone: true
})
@Injectable({
  providedIn: 'root'
})
export class GeocodingComponent implements OnInit {

  @ViewChild("trigger") trigger: MatAutocompleteTrigger;
  @ViewChild("formOrigin") formOrigin: HTMLElement;
  @ViewChild("matInput") matInput: MatInput;
  @ViewChild("searchInputEl") searchInputEl: ElementRef<HTMLInputElement>;

  readonly openOnLargeScreen = input<boolean>(false);
  readonly mapProjection = input<string>();

  protected overlayRef: OverlayRef;
  public geocodingInput: string;
  private coords: TTCoordinate = null;
  private foundedSheet: Feature;
  private searchSheetResult: Feature = null;
  public filteredOptions: Observable<any> = null;
  public geocodingOpenCloseEvent = new Subject<any>();
  // public autoCompleteControl = new UntypedFormControl();
  public autoCompleteControl = new UntypedFormControl();
  //!
  readonly isOpen = signal(false);
  readonly active = signal(false);
  readonly showSearchTip = signal(false);
  readonly loading = signal(false);
  readonly isValidCoords = signal<boolean | undefined>(undefined)
  private markerFeature: any;
  private subscriptions: Subscription = new Subscription();
  // static readonly coordsEnteredRegex = /(^[\d\.\, ]{3,}(?:_[A-Za-z]+)*$)/;
  static readonly coordsEnteredRegex = /(^[0-9])/;
  enteredCoords: string = null;
  private mapsheetsService: MapsheetsService;
  private mapindexService: MapindexService;
  public sheetsInMouseProjection: boolean = false;
  measureTooltip: OlTooltip;
  private ttLibConfig = inject<AppConfig>(TT_LIB_CONFIG);

  constructor(
    public overlay: Overlay,
    public mapService: MapService,
    private viewService: ViewService,
    private markerService: MarkerService,
    private popupService: PopupService,
    public coordinatesService: CoordinatesService,
    private injector: Injector,
    private toggleEventService: ToggleEventService,
    private backEventService: BackEventService,
    private translateService: TranslateService,
    private controlServices: ControlService,
    private alertService: AlertService,
    private browserSnifferService: BrowserSnifferService
  ) {
    this.sheetsInMouseProjection = this.ttLibConfig.map.controls.mousePosition.sheetsInMouseProjection;
    if (this.sheetsInMouseProjection) {
      // this.mapsheetsService = injector.get<MapsheetsService>(MapsheetsService);
      this.mapindexService = injector.get<MapindexService>(MapindexService);
    }
    effect(() => {
      this.isOpen.set(this.openOnLargeScreen());
    });

    this.subscriptions.add(
      this.toggleEventService.emitter$.subscribe(
        (toggleEvent: ToggleEventType) => {
          if (toggleEvent.forwardedFor === 'geocoding' && toggleEvent.eventName === ToggleEventNameType.CLOSE_COMMAND) {
            if (this.isOpen()) {
              this.clearInput();
              this.searchInputEl?.nativeElement.blur();
              this.onBlur();
            }
          }
        }
      )
    );

    this.subscriptions.add(
      toObservable(this.mapService.wasContextMenuEvent).subscribe((wasTapHoldEvent: boolean) => {
        if (wasTapHoldEvent && this.mapService.contextMenuCoordinate) {
          //small timeout so that other priority component could claim the contextMenu event
          window.setTimeout(() => {
            if (this.mapService.contextMenuCoordinate && this.mapService.contextMenuMapId && !this.mapService.contextMenuEventClaimed) {
              this.mapService.contextMenuEventClaimed = true;
              this.showContextMenu(null);
            }
          }, 100);
        }
      })
    );
    this.subscriptions.add(this.popupService.customEvent.subscribe((customEventObj) => {
      if (customEventObj && customEventObj.detail && customEventObj.detail.eventId === 'ContextMenuPopupCopy' && customEventObj.detail.data && customEventObj.detail.data.coordinate) {
        this.copyCoordinateToClipboard(customEventObj.detail.data.coordinate);
        //this.popupService.hide();
      }
    }));
  }

  /**
   * HIGHLIGHT CHARS:
   * https://stackblitz.com/edit/angular-mat-autocomplete-highlight?file=app%2Fautocomplete-overview-example.ts
   * 
   */

  ngOnInit() {
    // console.log('init geocoding');
    /*
    when mapsheets are available in mouseposition, add them to the search function
     */

    // setTimeout(() => {
    // this.isOpen.set(this.openOnLargeScreen ? true : false);
    //   //   <input matInput placeholder="username" #usernameInput cdkFocusInitial>
    // }, 0);

    this.filteredOptions = this.autoCompleteControl.valueChanges.pipe(
      startWith(''),//Oostende//31N 713000, 5590000
      // delay emits
      // debounceTime(10),
      // use switch map so as to cancel previous subscribed events, before creating new once
      switchMap(value => {
        this.isValidCoords.set(undefined);
        // case : feature was seleted => value is an object
        if (typeof (value) === 'object') {
          return this.viewService.search(this.getFeatureName(value).toLowerCase().trim());
          // other cases : string typed => value is a string
        } else if ((typeof (value) === 'string') && value !== '' && (value.indexOf("°") > -1 || GeocodingComponent.coordsEnteredRegex.test(value.trim()))) {
          // this.validateEnteredCoords(value);
          this.handleNumberedInput(value);
          return of(null);
        } else if ((typeof (value) === 'string') && value !== '' && value.length > 1) {
          this.loading.set(true);
          return this.viewService.search(value.toLowerCase().trim());
        } else {
          return of(null);
        }
      }),
      tap(
        {
          next: () => {
            this.loading.set(false);
          }
        })
    );
  }

  ngAfterContentInit() {
  }

  ngAfterViewInit() {
    //listener markerclick
    if (this.mapService.mapClickOnMarker$) {
      this.subscriptions.add(this.mapService.mapClickOnMarker$.subscribe((marker) => this.handleMarkerClick(marker)));
    }
    //to close the tip without backdrop (backdrop lies over input)
    // const window = this._getWindow();
    // console.dir(window)
    // if (typeof window !== 'undefined') {
    //   () => window.document.addEventListener('blur', this._windowBlurHandler);
    // }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    if (this.overlayRef) { this.overlayRef.dispose(); }
  }

  ontoggle() {
    (this.isOpen()) ? this.hideInput() : this.openInput();
  }

  openInput() {
    this.showSearchTip.set(false);
    this.isOpen.set(true);
    this.geocodingOpenCloseEvent.next(this.isOpen());
    setTimeout(() => {
      // document.getElementById('searchInput').focus();
      this.matInput.focus();
      if (this.autoCompleteControl.value) {
        this.autoCompleteControl.setValue(this.autoCompleteControl.value)
      }
    }, 300)
  }

  hideInput() {
    // document.getElementById('searchInput')!.blur();
    this.searchInputEl?.nativeElement.blur();
    this.trigger?.closePanel();
    this.isOpen.set(false);
    this.geocodingOpenCloseEvent.next(this.isOpen());
    this.showSearchTip.set(false);
    this.active.set(false);
  }

  clearOrHideInput($event: Event) {
    if (this.autoCompleteControl.value !== null && this.autoCompleteControl.value !== '') {
      this.clearInput();
    } else {
      this.hideInput();
    }
  }

  clearInput() {
    this.autoCompleteControl.setValue('');
    if (this.markerFeature && this.markerService.hasThisMarker(this.markerFeature)) {
      this.markerService.remove(this.markerFeature);
      if (this.popupService.getPopupType() && this.popupService.getPopupType() === "geocoding") {
        this.popupService.remove()
      };
    }
    if (this.foundedSheet && this.mapindexService.hasThisSheet(this.foundedSheet)) {
      this.mapindexService.remove(this.foundedSheet);
      if (this.popupService.getPopupType() && this.popupService.getPopupType() === "geocoding") {
        this.popupService.remove()
      };
    }
  }

  onFocus(evt) {
    this.active.set(true);
    // if empty?    
    if (!this.trigger.panelOpen) {
      if (GeocodingComponent.coordsEnteredRegex.test(this.autoCompleteControl.value)) {
        this.handleNumberedInput(this.autoCompleteControl.value);
      }
      if (!this.autoCompleteControl.value) {
        this.showSearchTip.set(true);
      }

    }
    //evt.stopPropagation();

    this.backEventService.registerOpenedWindow({ windowName: 'geocoding', isAutoCloseableWindow: false });
  }

  onBlur() {
    if (!this.trigger.panelOpen) {
      this.active.set(false);
      this.showSearchTip.set(false);
    }
  }

  onPanelOpened() {
    this.showSearchTip.set(false);
    this.active.set(true);
  }

  onPanelClosed() {
    this.showSearchTip.set(false);
    this.active.set(false);
  }

  searchTipOutSideClick(evt) {
    if (this.matInput.focused) {
      return;
    }
    if (!document.getElementById('geocodingForm').contains(evt.target)) {
      this.showSearchTip.set(false); this.active.set(false); this.isValidCoords.set(undefined);
    }
    this.showSearchTip.set(false); this.active.set(false); this.isValidCoords.set(undefined);
  }

  onSelect(selectedItem: any) {
    // document.getElementById('searchInput').blur();
    this.searchInputEl?.nativeElement.blur();
    if (this.foundedSheet) {
      if (this.mapindexService.hasThisSheet(this.foundedSheet)) {
        this.mapindexService.remove(this.foundedSheet)
      }
    }
    if (this.markerFeature) {
      if (this.markerService.hasThisMarker(this.markerFeature)) {
        this.markerService.remove(this.markerFeature)
      }
    }
    this.markerFeature = new GeoJSON().readFeature(selectedItem);
    //set type, classification, styleClass and highlight of marker
    this.markerFeature.set('type', 'geocoding');
    this.markerFeature.set('classification', this.markerFeature.get('locationtype'));
    this.markerFeature.set('styleClass', this.markerFeature.get('locationtype'));
    this.markerFeature.set('highlight', true);
    this.zoomToFeature();
  }

  //coords or mapsheets
  onNumberedInputKeyUp(event: Event) {
    if (this.isValidCoords()) {
      // document.getElementById('searchInput').blur();
      this.searchInputEl?.nativeElement.blur();
      if (this.foundedSheet) {
        if (this.mapindexService.hasThisSheet(this.foundedSheet)) {
          this.mapindexService.remove(this.foundedSheet);
          if (this.popupService.getPopupType() && this.popupService.getPopupType() === "geocoding") {
            this.popupService.remove()
          };
        }
      }
      if (this.markerFeature) {
        if (this.markerService.hasThisMarker(this.markerFeature)) {
          this.markerService.remove(this.markerFeature);
        }
        if (this.popupService.getPopupType() && this.popupService.getPopupType() === "geocoding") {
          this.popupService.remove()
        };
      }
      //mapsheet found
      if (this.sheetsInMouseProjection && this.searchSheetResult) {
        this.zoomToSheetFeature();
        this.isValidCoords.set(undefined);
        this.showSearchTip.set(false);
        return;
      }
      //coords found
      var featureAsJSON = {
        'type': 'Feature',
        'geometry': {
          'type': 'Point',
          'coordinates': this.coords?.getCoordinate(),
        },
        'properties': {
          'locationtype': 'coords-' + this.coords.getCoordinateSystem().toLowerCase(),
          "name": this.coords?.getCoordinateAsString(),
        }
      };
      this.markerFeature = <Feature>new GeoJSON().readFeature(featureAsJSON);
      this.markerFeature.set('type', 'geocoding');
      this.markerFeature.set('classification', this.markerFeature.get('locationtype'));
      this.markerFeature.set('styleClass', 'coords');
      // this.markerFeature.set('styleClass', this.markerFeature.get('locationtype'));
      this.markerFeature.set('highlight', true);
      this.isValidCoords.set(undefined);
      this.showSearchTip.set(false);
      this.active.set(false);
      this.zoomToFeature();
    }
  }

  onPrefixBtnClick(evt) {
    (this.isValidCoords()) ? this.onNumberedInputKeyUp(evt) : this.hideInput();
    evt.stopPropagation();
  }

  private handleNumberedInput(value: string) {
    /*    
    check if an sheet is found (return sheet feature), if no sheet go checking coords (return coords obj)    
    */
    if (this.sheetsInMouseProjection) {
      this.searchSheetResult = null;
      this.searchSheetResult = this.mapindexService.searchMapindex(value);
      // console.log('mapsheet: ' + value)
      if (this.searchSheetResult) {
        this.isValidCoords.set(true);
        return;
      }
    }
    this.validateEnteredCoords(value);
  }

  private validateEnteredCoords(value: string) {
    //returns TTcoord obj if valid or null
    this.coords = this.coordinatesService.handleCoords(value.trim(), this.viewService.getProjection().getCode());
    if (this.coords !== null && this.coords.getCoordinateSystem() !== null) {
      this.isValidCoords.set(true);
    } else {
      this.isValidCoords.set(false);
    }
  }

  getFeatureName(value?: any): any | undefined {
    if (value) {
      if (value.properties) {
        return value.properties.name;
        //  return   value.properties.data.street.default + ' ' + value.properties.data.housenumber + ', ' +  value.properties.data.municipality.default 
      }
      else {
        return value;
      }
      return value.properties.name == value.properties.name ? value.properties.name : '';
      // return value && value.properties.name == value.properties.name ? value.properties.name : '';
    }
  }

  private zoomToSheetFeature() {

    //geolocation is listening for recenter
    this.toggleEventService.toggleEvent({
      forwardedFor: 'geocoding-featurezoom',
      eventName: ToggleEventNameType.ACTIVE
    });

    this.foundedSheet = this.searchSheetResult.clone();
    let sheetType: string;
    //get type
    if (this.mapindexService.mapindexLvl_0_List.includes(this.searchSheetResult)) {
      sheetType = '50k'
    } else if (this.mapindexService.mapindexLvl_1_List.includes(this.searchSheetResult)) {
      sheetType = '25k'
    }
    //  else if (this.mapindexService.mapindexLvl__List.includes(this.searchSheetResult)) {
    //   sheetType = '10k'
    // }
    // if (this.mapsheetsService.mapsheets50kList.includes(this.searchSheetResult)) {
    //   sheetType = '50k'
    // } else if (this.mapsheetsService.mapsheets25kList.includes(this.searchSheetResult)) {
    //   sheetType = '25k'
    // } else if (this.mapsheetsService.mapsheets10kList.includes(this.searchSheetResult)) {
    //   sheetType = '10k'
    // }
    delete this.searchSheetResult;
    this.foundedSheet.set('sheetType', sheetType);
    this.viewService.zoomToFeatureExtent(this.foundedSheet.getGeometry(), [50, 50, 50, 50],
      //function after animating is done
      () => {
        setTimeout(() => {
          this.mapindexService.add(this.foundedSheet);
          this.mapindexService.displaySheetPopup(this.foundedSheet);
        }, 100);

        this.toggleEventService.toggleEvent({
          forwardedFor: 'geocoding-featurezoom',
          eventName: ToggleEventNameType.INACTIVE
        });
      }
    );
  }

  private zoomToFeature() {

    //geolocation is listening for recenter
    this.toggleEventService.toggleEvent({
      forwardedFor: 'geocoding-featurezoom',
      eventName: ToggleEventNameType.ACTIVE
    });

    //depending on classification
    let zoomlevel: number = this.getZoomlevel(this.markerFeature.get('classification'));

    this.viewService.zoomAndPan(
      (<Point>this.markerFeature.getGeometry()).getCoordinates()[0],
      (<Point>this.markerFeature.getGeometry()).getCoordinates()[1],
      zoomlevel,
      //function after animating is done
      () => {
        setTimeout(() => {
          this.markerService.add(this.markerFeature);
          this.displayPopup(this.markerFeature);
        }, 100);

        this.toggleEventService.toggleEvent({
          forwardedFor: 'geocoding-featurezoom',
          eventName: ToggleEventNameType.INACTIVE
        });
      }
    );
  }

  private displayPopup(marker: Feature, mapId?: string) {
    let point: Point = <Point>marker.getGeometry();
    //depending on classification
    let zoomlevel = this.getZoomlevel(marker.get('classification'));
    let popupProperties: PopupData =
    {
      coordinates: point.getCoordinates(),
      type: marker.get('type'),
      title: 'geocoding.classification-' + marker.get('classification'),
      content: this.getPopupContent(marker),
      //showFeatureElevation: marker.get('classification') && marker.get('classification').indexOf('coords-') > -1,
      showFeatureElevation: false,
      zoomToFeatureLink: true,
      zoomlevel: zoomlevel,
      mapId: mapId
      //className: ''
    }
    this.popupService.add(popupProperties);
  }

  private getZoomlevel(classification) {
    let zoomlevel //=  (this.projectionCode === 'EPSG:3812')? 9 : 17
    if (this.viewService.getProjection().getCode() === 'EPSG:3812') {
      switch (classification) {
        case 'toponym':
          zoomlevel = 8
          break;
        case 'street':
          zoomlevel = 9
          break;
        case 'address':
          zoomlevel = 10
          break;

        default:
          zoomlevel = 9
          break;
      }
    }
    else {
      switch (classification) {
        case 'toponym':
          zoomlevel = 15
          break;
        case 'street':
          zoomlevel = 16
          break;
        case 'address':
          zoomlevel = 17
          break;
        default:
          zoomlevel = 16
          break;
      }
    }
    return zoomlevel;
  }

  private getPopupContent(feature: Feature): string {
    let content: string;
    if (feature.get('locationtype').indexOf('coords') > -1) {
      content = feature.get('name');
    } else {
      let housenumber = feature.get('data').housenumber || '';
      let street = feature.get('data').street.default || ''; //also name toponym here...
      let postalcode = feature.get('data').postalcode || '';
      let municipalitysection = feature.get('data').municipalitysection.default || '';
      let municipality = feature.get('data').municipality.default || '';
      content = street + ((housenumber) ? ' ' + housenumber + ', ' : ', ')
        + postalcode + '&nbsp;' + municipalitysection + ((municipality) ? ((municipality !== municipalitysection) ? ', ' + municipality : '') : '');
    }
    return content
  }

  private handleMarkerClick(marker: Feature) {
    if (marker && marker.get('type') === 'geocoding' && !marker.get('highlight')) {
      this.displayPopup(marker, this.mapService.contextMenuMapId);
    } else if (marker && marker.get('type') === 'ContextMenuFeature' && !marker.get('highlight')) {
      this.showContextMenu(marker);
    }
  }

  public getInputIcon(): string {
    // const iconNames = ['done', 'report_problem', 'arrow_forward'];
    const iconNames = ['mood', 'mood_bad', 'arrow_forward'];
    return this.getValidCoordsProperty(iconNames);
  }

  public getInputIconTitle(): string {
    const titleKeys = ['geocoding.valid_coords', 'geocoding.notvalid_coords', 'general.close'];
    return this.getValidCoordsProperty(titleKeys);
  }

  private getValidCoordsProperty(props: string[]) {
    let val: string;
    if (this.isValidCoords() === true) {
      val = props[0];
    } else if (this.isValidCoords() === false) {
      val = props[1];
    } else { // if undefined
      val = props[2];
    }
    return val;
  }

  private async copyCoordinateToClipboard(coordinate: olCoordinate.Coordinate | any) {
    try {
      // await navigator.clipboard.writeText(olCoordinate.toStringXY(coordinate, this.controlServices.activeProj().precision));
      await navigator.clipboard.writeText(coordinate);
      this.alertService.success("share.copied", { "autoClose": true });
      // console.log('Coordinates copied to clipboard');
    } catch (err) {
      this.alertService.warn("share.copied-failed", { "autoClose": true })
      console.error('Coordinates copy error: ', err);
    }
  }

  /**
   * 
   * @param marker null when it's a new marker on the map, otherwise the user clicked on the old marker again
   */
  public async showContextMenu(marker: Feature|null) {
    let dataToCopy: any;

    const contextMenuCoordinate = marker ? (marker.getGeometry() as Point).getCoordinates() : this.mapService.contextMenuCoordinate;

    //when you can select the projection in the mouse-position component, then the selected projection is shown
    if (!this.browserSnifferService.isPhone()) {
      if (this.controlServices.activeProj().epsg === 'mapindex') {
        const contextMenuMap = this.mapService.contextMenuMapId === 'main' ? this.mapService.getMap() : this.mapService.getMap(this.mapService.contextMenuMapId);
        const customContextMenuEventForMapIndex = {
          type: 'CustomContextMenuEventForMapIndex',
          target: contextMenuMap,
          map: contextMenuMap,
          coordinate: contextMenuCoordinate,
          pixel: contextMenuMap.getPixelFromCoordinate(contextMenuCoordinate)
        } as MapBrowserEvent;
        dataToCopy = this.controlServices.getCellNameFromCoord(customContextMenuEventForMapIndex);
        this.coords = new TTCoordinate(contextMenuCoordinate, 'mapindex', dataToCopy)
      } else {
        if (this.viewService.getProjection().getCode() === this.controlServices.activeProj().epsg) {
          dataToCopy = olCoordinate.toStringXY(contextMenuCoordinate, this.controlServices.activeProj().precision);
        } else {
          const coords = transform(contextMenuCoordinate, this.viewService.getProjection().getCode(), this.controlServices.activeProj().epsg);
          dataToCopy = olCoordinate.toStringXY(coords, this.controlServices.activeProj().precision);
          if (this.controlServices.activeProj().name === 'UTM') {
            dataToCopy = `${this.controlServices.activeUtmZone()}N ${dataToCopy}`;
          }
        }
        this.coords = this.coordinatesService.handleCoords(dataToCopy, this.controlServices.activeProj().epsg);
      }
    }
    //when you can't select the projection (mouse-position component hidden), then EPSG:4326 is always shown 
    else {
      const lonLat = transform(contextMenuCoordinate, this.viewService.getProjection().getCode(), 'EPSG:4326').reverse();
      dataToCopy = lonLat;
      this.coords = this.coordinatesService.handleCoords(lonLat.toString(), this.viewService.getProjection().getCode());
    }
  
    if (this.coords && dataToCopy) {
      if (marker) {
        this.markerFeature = marker;
      } else {
        var featureAsJSON = {
          'type': 'Feature',
          'geometry': {
            'type': 'Point',
            'coordinates': contextMenuCoordinate,
          },
          'properties': {
            'locationtype': 'coords-' + this.coords.getCoordinateSystem().toLowerCase(),
            "name": this.coords.getCoordinateAsString(),
          }
        };

        //done in the wasTapHoldEvent subscription above
        this.markerService.clearAll();

        this.markerFeature = <Feature>new GeoJSON().readFeature(featureAsJSON);
        this.markerFeature.set('type', 'ContextMenuFeature');
        this.markerFeature.set('classification', this.markerFeature.get('locationtype'));
        this.markerFeature.set('styleClass', 'coords');
        this.markerFeature.set('highlight', true);
        this.isValidCoords.set(undefined);
        //we don't zoom, we just show the popup where the user clicked
        this.markerService.add(this.markerFeature);
      }

      let popupProperties: PopupData = {
        coordinates: contextMenuCoordinate,
        type: 'ContextMenuPopup',
        title: 'geocoding.classification-' + this.markerFeature.get('locationtype'),
        // title: titleToDisplay,//'geocoding.classification-' + this.controlServices.activeProj().name,
        content: {
          'coordinate': this.coords.getCoordinateAsString(),
          'copyEvent': {
            'eventId': 'ContextMenuPopupCopy',
            'data': { coordinate: dataToCopy }
          }
        },
        className: 'ContextMenuPopup',
        showFeatureElevation: true,
        mapId: this.mapService.contextMenuMapId,
        zoomToFeatureLink: true,
        zoomlevel: this.getZoomlevel(this.markerFeature.get('classification'))
      };

      //making sure it's removed when the popup closes
      //marker service only removed highlighted features by default
      //const markerFeatureRef = this.markerFeature;
      //this.popupService.onPopupDestroy$.pipe(take(1)).subscribe(() => { this.markerService.remove(markerFeatureRef); })
      this.popupService.add(popupProperties);
    }
  }
}
